import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { AumicoCoreProvider } from '@modeso/aumico-lib-core-fe';
import { ICompanyDTO, ICompanyNameIdLegalFormDTO, ICompanyStatusDTO } from '@modeso/types__aumico-lib-companies-be';
import Debug from 'debug';
import { Observable, finalize } from 'rxjs';
import { FileMetadataDto } from '../../../../aumico-project-reports/src/app/microservices/ms-annual-reports/financial-closing-process/dto/file-metadata.dto';
import { AumicoCompaniesConfigService } from '../aumico-companies.config';
import { ICompanyExportDto } from '../dtos/company-export-dto.interface';
import { ICompanyExportTableHeaders } from '../dtos/company-export-table-header-dto.interface';
import { CompanyValuationRequestDTO } from '../dtos/company-valuation-request-dto.interface';
import { TasksUpdate } from '../enums/tasks-update.enum';
const debug = Debug('modeso:aumico-companies:AumicoCompaniesService');

const httpOptions = {
    headers: new HttpHeaders({
        'Content-Type': 'application/json',
    }),
};

@Injectable({
    providedIn: 'root',
})
export class AumicoCompaniesService {
    private server = '';
    private microserviceName = 'companies';
    private readonly fileMetadata = 'file-metadata';

    constructor(
        private http: HttpClient,
        @Inject(AumicoCompaniesConfigService) private config,
        private coreProvider: AumicoCoreProvider,
    ) {
        this.server = config.apiGateway;
    }

    createCompany(company: ICompanyDTO): Observable<ICompanyDTO> {
        debug('create new company');
        const url = `${this.server}${this.microserviceName}`;
        this.coreProvider.dispatchBlockingRequest('createCompany');
        return this.http
            .post<ICompanyDTO>(url, company)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('createCompany')));
    }

    public updateCompany(company: ICompanyDTO, tasksUpdate?: TasksUpdate) {
        debug('updateCompany called');
        const url = `${this.server}${this.microserviceName}/${company._id}`;
        delete company._id;
        this.coreProvider.dispatchBlockingRequest('updateCompany');
        return this.http
            .put<ICompanyDTO>(url, { company, tasksUpdate }, httpOptions)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('updateCompany')));
    }

    public bulkUpdateCompanies(companies: ICompanyDTO[], logo: any | null, tasksUpdate?: TasksUpdate): Observable<ICompanyDTO[]> {
        debug('bulkUpdateCompanies called');
        const url = `${this.server}${this.microserviceName}/bulk-update`;
        const payload = { companies, logo, tasksUpdate };
        this.coreProvider.dispatchBlockingRequest('bulkUpdateCompanies');
        return this.http
            .put<ICompanyDTO[]>(url, payload, httpOptions)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('bulkUpdateCompanies')));
    }

    public updateCompanyStatus(statusUpdate: ICompanyStatusDTO) {
        debug('updateCompanyStatus called');
        const url = `${this.server}${this.microserviceName}/${statusUpdate._id}/status`;
        this.coreProvider.dispatchBlockingRequest('updateCompanyStatus');
        return this.http
            .put<ICompanyDTO>(url, statusUpdate, httpOptions)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('updateCompanyStatus')));
    }

    /** Sends only the consent acknowledgement; the backend assigns the dates and enforces ORG_ADMIN. */
    public activateFx(companyId: string): Observable<ICompanyDTO> {
        debug('activateFx called');
        const url = `${this.server}${this.microserviceName}/${companyId}/fx/activate`;
        this.coreProvider.dispatchBlockingRequest('activateFx');
        return this.http
            .patch<ICompanyDTO>(url, { consentAccepted: true }, httpOptions)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('activateFx')));
    }

    public deactivateFx(companyId: string): Observable<ICompanyDTO> {
        debug('deactivateFx called');
        const url = `${this.server}${this.microserviceName}/${companyId}/fx/deactivate`;
        this.coreProvider.dispatchBlockingRequest('deactivateFx');
        return this.http
            .patch<ICompanyDTO>(url, {}, httpOptions)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('deactivateFx')));
    }

    public deleteCompany(payload: { _id: string }) {
        debug('delete company called');
        const url = `${this.server}${this.microserviceName}/${payload._id}`;
        this.coreProvider.dispatchBlockingRequest('deleteCompany');
        return this.http
            .delete<ICompanyDTO>(url, httpOptions)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('deleteCompany')));
    }

    getCompanies(): Observable<ICompanyDTO[]> {
        debug('getCompanies called');
        const url = `${this.server}${this.microserviceName}`;
        this.coreProvider.dispatchBlockingRequest('getCompanies');
        return this.http
            .get<ICompanyDTO[]>(url)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('getCompanies')));
    }

    getCompaniesNameIdLegalForm(): Observable<ICompanyNameIdLegalFormDTO[]> {
        debug('getCompaniesNameIdLegalForm called');
        const url = `${this.server}${this.microserviceName}`;
        let params = new HttpParams();
        params = params.append('onlyNameIdLegalForm', true);
        this.coreProvider.dispatchBlockingRequest('getCompaniesNameIdLegalForm');
        return this.http
            .get<ICompanyNameIdLegalFormDTO[]>(url, { params: params })
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('getCompaniesNameIdLegalForm')));
    }

    getCompanyById(id: string): Observable<ICompanyDTO> {
        debug('getCompanyById called');
        const url = `${this.server}${this.microserviceName}/${id}`;
        this.coreProvider.dispatchBlockingRequest('getCompanyById');
        return this.http
            .get<ICompanyDTO>(url)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('getCompanyById')));
    }

    companyValuation(companyValuationRequest: CompanyValuationRequestDTO[]) {
        const companyValuationRequestObject = {
            valuationRequests: companyValuationRequest,
        };
        debug('companyValuationRequest called');
        const url = `${this.server}valuation-request`;
        this.coreProvider.dispatchBlockingRequest('companyValuationRequest');
        return this.http
            .post<CompanyValuationRequestDTO>(url, companyValuationRequestObject, httpOptions)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('companyValuationRequest')));
    }

    downloadDefaultValuationFile$(): Observable<any> {
        const url = `${this.server}valuation-request/default`;
        this.coreProvider.dispatchBlockingRequest('downloadDefaultValuationFile$');
        return this.http
            .get(url, { responseType: 'blob' })
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('downloadDefaultValuationFile$')));
    }

    public uploadCompanyLogo(file: File): Observable<FileMetadataDto> {
        const url = `${this.server}${this.fileMetadata}/upload`;
        const formData = new FormData();
        formData.append('file', file);
        this.coreProvider.dispatchBlockingRequest('uploadCompanyLogo');
        return this.http
            .post<FileMetadataDto>(url, formData)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('uploadCompanyLogo')));
    }

    public exportCompaniesDataAsExcel$(payload: {
        tableHeaders: ICompanyExportTableHeaders;
        companies: ICompanyExportDto[];
    }): Observable<any> {
        const url = `${this.server}exporting/companies-xlsx`;
        this.coreProvider.dispatchBlockingRequest('exportCompaniesDataAsExcel$');
        return this.http
            .post(url, payload, { responseType: 'blob', observe: 'body' })
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('exportCompaniesDataAsExcel$')));
    }
}
